import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Newsletter popup custom element.
 *
 * Reproduces the legacy newsletter modal behaviour with a modern web component:
 * - Shows the popup after a configurable delay (different delay for returning visitors).
 * - Persists "seen" state per session and across visits, and suppresses the popup
 *   entirely once the visitor has subscribed.
 * - Slides in from the bottom-right with an overlay on desktop, closes on the close
 *   button, the Escape key or an overlay click.
 * - Expands the privacy text via an optional "read more" button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overlay
 * @property {HTMLElement} dialog
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLButtonElement} [privacyReadMore]
 * @property {HTMLElement} [privacyShort]
 * @property {HTMLElement} [privacyFull]
 *
 * @extends {Component<Refs>}
 */
export class NewsletterPopup extends Component {
  requiredRefs = ['overlay', 'dialog', 'closeButton'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval = undefined;

  /** @type {Element | null} */
  #previousFocus = null;

  static storageKeys = {
    sessionTimer: 'newsletterPopupTimer',
    sessionSeen: 'newsletterPopupSeen',
    seen: 'popupSeen',
    subscribed: 'userSubscribed',
  };

  connectedCallback() {
    super.connectedCallback();

    this.refs.closeButton.addEventListener('click', this.hide);
    this.refs.overlay.addEventListener('click', this.hide);
    this.refs.privacyReadMore?.addEventListener('click', this.#expandPrivacy);

    if (this.subscribed) return;

    // If the form was just submitted successfully, mark the visitor as subscribed
    // and keep the popup open so the success message is visible.
    if (this.querySelector('[data-submitted]')) {
      this.#setStorage(localStorage, NewsletterPopup.storageKeys.subscribed, '1');
      this.#open();
      return;
    }

    this.#startTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#interval);
    this.refs.closeButton.removeEventListener('click', this.hide);
    this.refs.overlay.removeEventListener('click', this.hide);
    this.refs.privacyReadMore?.removeEventListener('click', this.#expandPrivacy);
    document.removeEventListener('keyup', this.#handleKeyup);
  }

  get subscribed() {
    return this.#getStorage(localStorage, NewsletterPopup.storageKeys.subscribed) === '1';
  }

  get time() {
    return parseInt(this.getAttribute('data-time') ?? '0', 10) || 0;
  }

  get revisitTime() {
    return parseInt(this.getAttribute('data-revisit-time') ?? '0', 10) || 0;
  }

  #startTimer() {
    const { storageKeys } = NewsletterPopup;
    const hasSeenBefore = this.#getStorage(localStorage, storageKeys.seen) === '1';
    const target = hasSeenBefore ? this.revisitTime : this.time;

    this.#interval = setInterval(() => {
      const elapsed = parseInt(this.#getStorage(sessionStorage, storageKeys.sessionTimer) ?? '0', 10) + 1000;

      if (elapsed < target) {
        this.#setStorage(sessionStorage, storageKeys.sessionTimer, `${elapsed}`);
        return;
      }

      clearInterval(this.#interval);

      if (this.#getStorage(sessionStorage, storageKeys.sessionSeen) === '1') return;

      this.show();
      this.#setStorage(localStorage, storageKeys.seen, '1');
      this.#setStorage(sessionStorage, storageKeys.sessionSeen, '1');
      this.#setStorage(sessionStorage, storageKeys.sessionTimer, '0');
    }, 1000);
  }

  /**
   * Shows the popup.
   */
  show = () => {
    if (this.subscribed) return;
    this.#open();
  };

  #open() {
    const active = document.activeElement;
    this.#previousFocus = active instanceof HTMLElement ? active : null;

    this.setAttribute('open', '');
    document.body.classList.add('newsletter-popup-open');
    document.addEventListener('keyup', this.#handleKeyup);

    requestAnimationFrame(() => {
      this.refs.closeButton.focus();
    });
  }

  /**
   * Hides the popup with the closing animation.
   */
  hide = async () => {
    if (!this.hasAttribute('open')) return;

    document.removeEventListener('keyup', this.#handleKeyup);
    this.setAttribute('closing', '');

    await onAnimationEnd(this.refs.dialog, undefined, { subtree: false });

    this.removeAttribute('open');
    this.removeAttribute('closing');
    document.body.classList.remove('newsletter-popup-open');

    if (this.#previousFocus instanceof HTMLElement) {
      this.#previousFocus.focus();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyup = (event) => {
    if (event.key === 'Escape') this.hide();
  };

  /**
   * @param {Event} event
   */
  #expandPrivacy = (event) => {
    event.preventDefault();
    const { privacyShort, privacyFull, privacyReadMore } = this.refs;
    if (privacyShort) privacyShort.hidden = true;
    if (privacyFull) privacyFull.hidden = false;
    if (privacyReadMore) privacyReadMore.hidden = true;
  };

  /**
   * @param {Storage} storage
   * @param {string} key
   * @returns {string | null}
   */
  #getStorage(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   * @param {string} value
   */
  #setStorage(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch {
      // Storage may be unavailable (private mode); fail silently.
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
